import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { villaData } from "../data/villaData";
import { commonData } from "../data/villaMapVideoCTA";
import { ArrowLeft, ArrowRight, Share2,MapPin } from "lucide-react";
import SeoHelper from "../common/SeoHelper";

const VillaDetail = () => {
  const { villaId } = useParams();
  const navigate = useNavigate();

  const villa = villaData.villas.find((v) => v.id === villaId);

  // Hooks must be BEFORE any return
  const allImages = useMemo(
    () => [villa?.image, ...(villa?.images || [])].filter(Boolean),
    [villa]
  );

  const [currentIndex, setCurrentIndex] = useState(0);

  if (!villa) {
    return (
      <div className="text-center py-20">
        <h1 className="text-2xl font-bold">Villa not found</h1>
        <button
          onClick={() => navigate("/")}
          className="mt-4 text-emerald-700 underline"
        >
          Go back
        </button>
      </div>
    );
  }

  const prevImage = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  const nextImage = () => {
    setCurrentIndex((prev) =>
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  const handleShare = async () => {
    const shareData = {
      title: villa.detail.title,
      text: "Check out this premium villa at Floret Town",
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {}
    } else {
      await navigator.clipboard.writeText(shareData.url);
      alert("Link copied! Share it anywhere.");
    }
  };

  return (
    <>
     <SeoHelper />
    
    <div className="max-w-7xl mx-auto px-4 py-24 space-y-14">

      {/* TOP */}
      <div className="border-b pb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {villa.detail.title}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            <MapPin size={20} className="inline mr-3"/>{commonData.map.caption}
          </p>
        </div>

        <button
          onClick={handleShare}
          className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
        >
          <Share2 size={18} className="inline mr-2"/>
          Share
        </button>
      </div>

      {/* IMAGES */}
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="relative">
            <img
              src={allImages[currentIndex]}
              alt="Villa"
              className="w-full h-[260px] sm:h-[360px] lg:h-[420px] object-cover rounded-2xl shadow"
            />

            <button
              onClick={prevImage}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow"
            >
              <ArrowLeft size={20} />
            </button>

            <button
              onClick={nextImage}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow"
            >
              <ArrowRight size={20} />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-5 gap-2">
            {allImages.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`Thumbnail ${idx + 1}`}
                onClick={() => setCurrentIndex(idx)}
                className={`h-16 sm:h-20 w-full object-cover rounded-lg cursor-pointer border-2
                  ${
                    currentIndex === idx
                      ? "border-emerald-600"
                      : "border-transparent hover:border-emerald-300"
                  }`}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="text-gray-700 leading-relaxed">
            {villa.detail.intro}
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <span className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full font-semibold">
              {villa.areaSqFt} sq.ft
            </span>
            <span className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full font-semibold">
              {villa.bhk} BHK
            </span>
            <span className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full font-semibold">
              {villa.villaType}
            </span>
          </div>

          <button
            onClick={() =>
              navigate(commonData.cta.redirect, {
                state: { villaType: villa.villaType },
              })
            }
            className="mt-8 w-full bg-emerald-700 hover:bg-emerald-800 text-white py-3 rounded-xl font-semibold"
          >
            {commonData.cta.buttonText}
          </button>
        </div>
      </div>

      {/* DETAILS */}
      <div className="space-y-10">
        {villa.detail.sections.map((sec, idx) => (
          <div key={idx}>
            <h2 className="text-2xl font-semibold mb-3">{sec.heading}</h2>
            {sec.content && <p className="text-gray-700">{sec.content}</p>}
            {sec.list && (
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                {sec.list.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* VIDEO */}
      <div>
        <h2 className="text-2xl font-semibold mb-4">Floret Town Walkthrough</h2>
        <div className="max-w-3xl aspect-video rounded-2xl overflow-hidden shadow">
          <iframe
            src={commonData.video.youtubeEmbed}
            title="Villa Walkthrough"
            allow="autoplay; fullscreen; encrypted-media"
            mute
            allowFullScreen
            className="w-full h-full"
          />
        </div>
        <p className="mt-3 text-gray-600 text-md">
    {commonData.video.caption}
  </p>
      </div>

        {/* MAP */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">Location</h2>
          <div className="max-w-3xl aspect-video rounded-2xl overflow-hidden shadow">
            <iframe
              src={commonData.map.iframe}
              title="Villa Location"
              loading="lazy"
              className="w-full h-full"
            />
          </div>
          <p className="mt-2 text-sm text-gray-600">
              <MapPin size={20} className="inline mr-3 text-red-500"/>{commonData.map.caption}
            </p>
        </div>
      </div>
      </>
  );
};

export default VillaDetail;